import { useState } from "react";
import { useParams, useSearchParams } from "react-router";
import {
  amsterdamDate,
  ROOM_MODE_LABELS,
  ROOM_MODES,
  VISIT_EVENT_LABELS,
  VISIT_SOURCE_LABELS,
  VISIT_STATUS_LABELS,
  type BuildingDto,
  type RoomMode,
  type VisitDto,
  type VisitSettingsDto,
} from "@klantpunt/shared";
import { Segmented, Tabs } from "../../app/Choice";
import { PanelHead, useSwipeClose } from "../../app/DetailPanel";
import { IconChevronLeft, IconChevronRight, IconPlus } from "../../app/icons";
import { Meta } from "../../app/Meta";
import { PageHeader } from "../../app/PageHeader";
import { Loading } from "../../app/Status";
import { api } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { useRealtime } from "../../lib/realtime";
import { useToast } from "../../lib/toast";
import { useBuilding } from "../building/useBuilding";
import { AnnounceDialog, CheckInDialog, CheckOutDialog, NoteDialog } from "./VisitDialogs";
import { errMsg, useVisitDay, useVisitEvents, useVisitPeople, useVisitsRefresh } from "./useVisits";

type Tab = "dag" | "aanwezig" | "instellingen";
type Filter = "alles" | "keuren" | "verwacht" | "binnen" | "controleren";
type Dialog =
  | { type: "new" }
  | { type: "checkin"; v: VisitDto }
  | { type: "checkout"; v: VisitDto }
  | { type: "note"; v: VisitDto; action: "reject" | "cancel" | "no-show" };

const TZ = "Europe/Amsterdam";
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit", timeZone: TZ });
const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: TZ,
  });
const fmtShort = (iso: string) =>
  new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", timeZone: TZ });
const fmtDay = (day: string) =>
  new Date(`${day}T12:00:00`).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });
const span = (v: VisitDto) =>
  v.endsAt ? `${fmtTime(v.startsAt)}–${fmtTime(v.endsAt)}` : `vanaf ${fmtTime(v.startsAt)}`;
const shiftDay = (day: string, n: number) => {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** Statuschip: Te keuren en Te controleren vragen aandacht, Binnen is groen, afgerond is neutraal. */
export function VisitChip({ v }: { v: VisitDto }) {
  if (v.needsCheck) return <span className="chip dir-cert dir-cert-expired">Te controleren</span>;
  if (v.status === "announced")
    return <span className="chip dir-cert dir-cert-warn">{VISIT_STATUS_LABELS.announced}</span>;
  if (v.status === "inside") return <span className="chip dir-cert dir-cert-ok">{VISIT_STATUS_LABELS.inside}</span>;
  return <span className="chip">{VISIT_STATUS_LABELS[v.status]}</span>;
}

const visitorLine = (v: VisitDto) =>
  [v.visitor.company, v.visitor.kind === "member" ? "eigen dienst" : null, v.purpose].filter(Boolean).join(" · ");

/**
 * Gebouw › Bezoek (docs/BEZOEK.md, issue #256): wie er vandaag komt, wie er nu binnen is en de instellingen van het
 * gebouw. Vooraf weten wie er komt (toegang, begeleiding, bewoners informeren); tijdens het bezoek weten wie er
 * binnen is (BHV, veiligheid, ontruiming).
 */
export function VisitsPage() {
  const { id = "" } = useParams();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const today = amsterdamDate(new Date());
  const tab = (["aanwezig", "instellingen"].includes(params.get("tab") ?? "") ? params.get("tab") : "dag") as Tab;
  const day = params.get("dag") ?? today;
  const sel = params.get("bezoek");
  const [filter, setFilter] = useState<Filter>("alles");
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const { data: b } = useBuilding(id);
  const q = useVisitDay(id, day === today ? null : day);
  useRealtime(id);
  const canEdit = hasModule(me, "visits", "edit");
  const canSettings = canEdit && can(me, "building.manage");

  const set = (k: string, v: string | null) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };

  if (!hasModule(me, "visits"))
    return (
      <div className="center">
        <p>Geen toegang tot de module Bezoek.</p>
      </div>
    );
  if (!b || !q.data) return <div className="center">{q.isError ? <p>{errMsg(q.error)}</p> : <Loading />}</div>;

  const { visits: all, settings } = q.data;
  const inside = all.filter((v) => v.status === "inside");
  const ofDay = all.filter((v) => amsterdamDate(new Date(v.startsAt)) === day || v.status === "inside" || v.needsCheck);
  const groups: Record<Filter, VisitDto[]> = {
    alles: ofDay,
    keuren: ofDay.filter((v) => v.status === "announced"),
    verwacht: ofDay.filter((v) => v.status === "approved"),
    binnen: inside,
    controleren: ofDay.filter((v) => v.needsCheck || v.needsConfirm),
  };
  const rows = tab === "aanwezig" ? inside : groups[filter];
  const current = sel ? (all.find((v) => v.id === sel) ?? null) : null;

  return (
    <div className={`page dir-page visit-page ${current && tab !== "instellingen" ? "has-sel" : ""}`}>
      <PageHeader
        label={
          <>
            Bezoek · <span className="code">{b.code}</span>
          </>
        }
        title={b.name}
        action={
          canEdit &&
          tab !== "instellingen" && (
            <button type="button" className="btn primary" onClick={() => setDialog({ type: "new" })}>
              <IconPlus size={20} aria-hidden="true" /> Bezoek
            </button>
          )
        }
      />
      <Tabs<Tab>
        label="Bezoek"
        value={tab}
        onChange={(t) => set("tab", t === "dag" ? null : t)}
        items={[
          { value: "dag", label: day === today ? "Vandaag" : fmtDay(day) },
          { value: "aanwezig", label: "Aanwezig", count: inside.length },
          ...(canSettings ? [{ value: "instellingen" as const, label: "Instellingen" }] : []),
        ]}
      />

      {tab === "instellingen" ? (
        <SettingsForm building={b} settings={settings} />
      ) : (
        <>
          {tab === "dag" ? (
            <div className="dir-filters">
              <div className="visit-day" role="group" aria-label="Dag">
                <button
                  type="button"
                  className="btn ghost ico"
                  aria-label="Vorige dag"
                  title="Vorige dag"
                  onClick={() => set("dag", shiftDay(day, -1))}
                >
                  <IconChevronLeft size={20} aria-hidden="true" />
                </button>
                <input
                  type="date"
                  aria-label="Kies een dag"
                  value={day}
                  onChange={(e) => set("dag", e.target.value && e.target.value !== today ? e.target.value : null)}
                />
                <button
                  type="button"
                  className="btn ghost ico"
                  aria-label="Volgende dag"
                  title="Volgende dag"
                  onClick={() => set("dag", shiftDay(day, 1))}
                >
                  <IconChevronRight size={20} aria-hidden="true" />
                </button>
                {day !== today && (
                  <button type="button" className="btn small ghost" onClick={() => set("dag", null)}>
                    Vandaag
                  </button>
                )}
              </div>
              <div className="dir-kinds" role="group" aria-label="Filter">
                {(
                  [
                    ["alles", "Alles"],
                    ["keuren", "Te keuren"],
                    ["verwacht", "Verwacht"],
                    ["binnen", "Binnen"],
                    ["controleren", "Te controleren"],
                  ] as const
                ).map(([k, label]) =>
                  k !== "alles" && k !== "binnen" && !groups[k].length ? null : (
                    <button
                      key={k}
                      type="button"
                      className={`kpi ${k === "keuren" || k === "controleren" ? "doc-attn" : ""}`}
                      aria-pressed={filter === k}
                      onClick={() => setFilter(k)}
                    >
                      {label} <span className="count num">{groups[k].length}</span>
                    </button>
                  ),
                )}
              </div>
            </div>
          ) : (
            <p className="muted small visit-lead">
              Iedereen die nu in {b.name} is ingeklokt. Gebruik deze lijst bij een ontruiming of BHV-melding.
            </p>
          )}
          <div className="dir-layout">
            <div className="dir-list tscroll">
              <table className="it" aria-label={tab === "aanwezig" ? "Aanwezig" : "Bezoek"}>
                <thead>
                  <tr>
                    <th scope="col">{tab === "aanwezig" ? "Binnen sinds" : "Tijd"}</th>
                    <th scope="col">Wie</th>
                    <th scope="col" className="dir-hide-narrow">
                      {tab === "aanwezig" ? "Ruimte" : "Gastheer"}
                    </th>
                    <th scope="col">Status</th>
                    {canEdit && (
                      <th scope="col" className="dir-hide-narrow">
                        <span className="sr-only">Actie</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((v) => (
                    <tr key={v.id} aria-current={sel === v.id} onClick={() => set("bezoek", v.id)}>
                      <td className="num small">
                        {tab === "aanwezig" && v.checkedInAt ? fmtTime(v.checkedInAt) : fmtTime(v.startsAt)}
                        {amsterdamDate(new Date(v.startsAt)) !== day && (
                          <span className="muted block">{fmtShort(v.startsAt)}</span>
                        )}
                      </td>
                      <td className="tcell-title">
                        <button type="button" className="linkbtn" onClick={() => set("bezoek", v.id)}>
                          {v.visitor.name}
                        </button>
                        <div className="muted small">
                          {tab === "aanwezig" && v.room ? `${v.room.name} · ${visitorLine(v)}` : visitorLine(v)}
                        </div>
                      </td>
                      <td className="dir-hide-narrow small">
                        {tab === "aanwezig"
                          ? (v.room?.name ?? "–")
                          : (v.host?.name ?? (v.visitor.kind === "member" ? "–" : "Beheerder"))}
                      </td>
                      <td>
                        <VisitChip v={v} />
                        {v.needsConfirm && <span className="chip dir-cert dir-cert-warn">Bevestigen</span>}
                      </td>
                      {canEdit && (
                        <td className="dir-hide-narrow" onClick={(e) => e.stopPropagation()}>
                          <NextAction v={v} building={b} settings={settings} onDialog={setDialog} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {!rows.length && (
                <p className="muted">
                  {tab === "aanwezig"
                    ? "Er is nu niemand ingeklokt."
                    : filter !== "alles"
                      ? "Niets in deze selectie."
                      : canEdit
                        ? "Geen bezoek op deze dag. Kondig aan wie er komt, dan weet de gastheer het op tijd."
                        : "Geen bezoek op deze dag."}
                </p>
              )}
            </div>
            {current && (
              <VisitPanel
                key={current.id}
                building={b}
                v={current}
                settings={settings}
                canEdit={canEdit}
                onClose={() => set("bezoek", null)}
                onDialog={setDialog}
              />
            )}
          </div>
        </>
      )}

      {dialog?.type === "new" && (
        <AnnounceDialog
          building={b}
          settings={settings}
          day={day < today ? today : day}
          onClose={() => setDialog(null)}
          onSaved={(v) => {
            const p = new URLSearchParams(params);
            p.set("bezoek", v.id);
            const d = amsterdamDate(new Date(v.startsAt));
            if (d === today) p.delete("dag");
            else p.set("dag", d);
            p.delete("tab");
            setParams(p, { replace: true });
          }}
        />
      )}
      {dialog?.type === "checkin" && (
        <CheckInDialog building={b} visit={dialog.v} settings={settings} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "checkout" && (
        <CheckOutDialog buildingId={b.id} visit={dialog.v} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "note" && (
        <NoteDialog buildingId={b.id} visit={dialog.v} action={dialog.action} onClose={() => setDialog(null)} />
      )}
    </div>
  );
}

/** Binnen melden zonder ruimte kan in één tik; met ruimte (of verplicht) via het venster. */
function useActions(v: VisitDto, b: BuildingDto, settings: VisitSettingsDto, onDialog: (d: Dialog) => void) {
  const toast = useToast();
  const refresh = useVisitsRefresh(b.id);
  const [busy, setBusy] = useState(false);
  const post = async (path: string, done: string) => {
    setBusy(true);
    try {
      await api(`/visits/${v.id}/${path}`, { method: "POST", body: {} });
      await refresh(v);
      toast(done);
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const checkIn = () =>
    settings.roomMode === "off" || (settings.roomMode === "optional" && !b.rooms.length)
      ? void post("check-in", `${v.visitor.name} is binnen`)
      : onDialog({ type: "checkin", v });
  return {
    busy,
    approve: () => void post("approve", "Bezoek goedgekeurd"),
    confirm: () => void post("confirm", "Bevestigd"),
    checkIn,
  };
}

/** De volgende stap voor dit bezoek, als kleine knop in de rij. */
function NextAction({
  v,
  building,
  settings,
  onDialog,
}: {
  v: VisitDto;
  building: BuildingDto;
  settings: VisitSettingsDto;
  onDialog: (d: Dialog) => void;
}) {
  const a = useActions(v, building, settings, onDialog);
  if (v.status === "announced" && v.canDecide)
    return (
      <button type="button" className="btn small" disabled={a.busy} onClick={a.approve}>
        Goedkeuren
      </button>
    );
  if (v.status === "approved")
    return (
      <button type="button" className="btn small" disabled={a.busy} onClick={a.checkIn}>
        Binnen
      </button>
    );
  if (v.status === "inside")
    return (
      <button type="button" className="btn small" onClick={() => onDialog({ type: "checkout", v })}>
        Vertrokken
      </button>
    );
  return null;
}

function VisitPanel({
  building: b,
  v,
  settings,
  canEdit,
  onClose,
  onDialog,
}: {
  building: BuildingDto;
  v: VisitDto;
  settings: VisitSettingsDto;
  canEdit: boolean;
  onClose: () => void;
  onDialog: (d: Dialog) => void;
}) {
  const events = useVisitEvents(v.id);
  const swipeRef = useSwipeClose<HTMLElement>(onClose);
  const a = useActions(v, b, settings, onDialog);
  const open = v.status === "announced" || v.status === "approved";
  return (
    <aside className="dir-panel" aria-label={v.visitor.name} ref={swipeRef}>
      <PanelHead
        chip={<VisitChip v={v} />}
        title={v.visitor.name}
        sub={<Meta items={[v.visitor.company, VISIT_SOURCE_LABELS[v.source], span(v)]} />}
        onClose={onClose}
      />
      {canEdit && (
        <section className="dir-sec">
          {v.status === "announced" && !v.canDecide && (
            <p className="muted small">Wacht op goedkeuring door {v.host?.name ?? "een gastheer of beheerder"}.</p>
          )}
          <div className="btnrow">
            {v.status === "announced" && v.canDecide && (
              <>
                <button type="button" className="btn small" disabled={a.busy} onClick={a.approve}>
                  Goedkeuren
                </button>
                <button
                  type="button"
                  className="btn small ghost"
                  onClick={() => onDialog({ type: "note", v, action: "reject" })}
                >
                  Afwijzen
                </button>
              </>
            )}
            {(v.status === "approved" || (v.status === "announced" && v.canDecide)) && (
              <button type="button" className="btn small" disabled={a.busy} onClick={a.checkIn}>
                Binnen melden
              </button>
            )}
            {v.status === "inside" && (
              <>
                <button type="button" className="btn small" onClick={() => onDialog({ type: "checkout", v })}>
                  Vertrokken
                </button>
                {settings.roomMode !== "off" && b.rooms.length > 0 && (
                  <button type="button" className="btn small ghost" onClick={() => onDialog({ type: "checkin", v })}>
                    Andere ruimte
                  </button>
                )}
              </>
            )}
            {v.needsConfirm && v.canDecide && (
              <button type="button" className="btn small" disabled={a.busy} onClick={a.confirm}>
                Bevestigen
              </button>
            )}
            {open && (
              <>
                <button
                  type="button"
                  className="btn small ghost"
                  onClick={() => onDialog({ type: "note", v, action: "no-show" })}
                >
                  Niet gekomen
                </button>
                <button
                  type="button"
                  className="btn small ghost"
                  onClick={() => onDialog({ type: "note", v, action: "cancel" })}
                >
                  Afzeggen
                </button>
              </>
            )}
          </div>
        </section>
      )}
      <section className="dir-sec">
        <h3>Bezoek</h3>
        <dl className="kv">
          <dt>Wanneer</dt>
          <dd>
            {fmtDay(amsterdamDate(new Date(v.startsAt)))}, {span(v)}
          </dd>
          {v.purpose && (
            <>
              <dt>Waarvoor</dt>
              <dd>{v.purpose}</dd>
            </>
          )}
          <dt>Gastheer</dt>
          <dd>
            {v.host?.name ?? (v.visitor.kind === "member" ? "Niet nodig (eigen dienst)" : "Gastheer of beheerder")}
          </dd>
          {v.escort && (
            <>
              <dt>Begeleider</dt>
              <dd>{v.escort.name}</dd>
            </>
          )}
          {(v.room || v.rooms.length > 0) && (
            <>
              <dt>{v.room ? "Nu in" : "Ruimte"}</dt>
              <dd>{(v.room ? [v.room] : v.rooms).map((r) => `${r.name} (${r.floor})`).join(", ")}</dd>
            </>
          )}
          {v.checkedInAt && (
            <>
              <dt>Binnen</dt>
              <dd>{fmtWhen(v.checkedInAt)}</dd>
            </>
          )}
          {v.checkedOutAt && (
            <>
              <dt>Vertrokken</dt>
              <dd>{fmtWhen(v.checkedOutAt)}</dd>
            </>
          )}
          {(v.accessNote ?? settings.accessNote) && (
            <>
              <dt>Toegang</dt>
              <dd className="dir-notes">{v.accessNote ?? settings.accessNote}</dd>
            </>
          )}
        </dl>
      </section>
      {(v.visitor.email || v.visitor.phone) && (
        <section className="dir-sec">
          <h3>Contact</h3>
          <dl className="kv">
            {v.visitor.phone && (
              <>
                <dt>Telefoon</dt>
                <dd>
                  <a href={`tel:${v.visitor.phone}`}>{v.visitor.phone}</a>
                </dd>
              </>
            )}
            {v.visitor.email && (
              <>
                <dt>E-mail</dt>
                <dd>
                  <a href={`mailto:${v.visitor.email}`}>{v.visitor.email}</a>
                </dd>
              </>
            )}
          </dl>
        </section>
      )}
      <section className="dir-sec">
        <h3>Logboek</h3>
        {events.data?.length ? (
          <ul className="dir-items">
            {events.data.map((e) => (
              <li key={e.id}>
                <span className="grow">
                  {VISIT_EVENT_LABELS[e.kind]}
                  <span className="muted small block">
                    {fmtWhen(e.at)}
                    {e.by && ` · ${e.by}`}
                    {e.note && ` · ${e.note}`}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        ) : events.isPending ? (
          <Loading small />
        ) : (
          <p className="muted small">Nog niets vastgelegd.</p>
        )}
      </section>
    </aside>
  );
}

/** Instellingen per gebouw: gastheren, toegang, wie zelf mag inklokken, ruimte bij inklokken en het controletijdstip. */
function SettingsForm({ building: b, settings: s }: { building: BuildingDto; settings: VisitSettingsDto }) {
  const toast = useToast();
  const refresh = useVisitsRefresh(b.id);
  const people = useVisitPeople(b.id);
  const [hosts, setHosts] = useState<string[]>(s.hosts.map((h) => h.id));
  const [accessNote, setAccessNote] = useState(s.accessNote ?? "");
  const [membersCheckIn, setMembersCheckIn] = useState(s.membersCheckIn);
  const [walkIns, setWalkIns] = useState(s.walkIns);
  const [roomMode, setRoomMode] = useState<RoomMode>(s.roomMode);
  const [lateAfter, setLateAfter] = useState(s.lateAfter);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await api(`/buildings/${b.id}/visit-settings`, {
        method: "PUT",
        body: { hostUserIds: hosts, accessNote, membersCheckIn, walkIns, roomMode, lateAfter },
      });
      await refresh();
      toast("Instellingen opgeslagen");
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="visit-settings">
      <section className="panel">
        <h2>Gastheren</h2>
        <p className="muted small">
          Een gastheer keurt aangekondigd bezoek goed en bevestigt wie zonder afspraak binnenloopt. Eigenaar en
          beheerder kunnen dat altijd. Zonder gastheren beslist de beheerder.
        </p>
        {people.data ? (
          <div className="visit-hosts">
            {people.data.map((p) => (
              <label key={p.id} className="check">
                <input
                  type="checkbox"
                  checked={hosts.includes(p.id)}
                  onChange={(e) => setHosts((h) => (e.target.checked ? [...h, p.id] : h.filter((x) => x !== p.id)))}
                />
                {p.name}
              </label>
            ))}
          </div>
        ) : (
          <Loading small />
        )}
      </section>
      <section className="panel">
        <h2>Inklokken</h2>
        <label className="check">
          <input type="checkbox" checked={membersCheckIn} onChange={(e) => setMembersCheckIn(e.target.checked)} />
          Collega's (eigen dienst) klokken zelf in, zonder goedkeuring
        </label>
        <label className="check">
          <input type="checkbox" checked={walkIns} onChange={(e) => setWalkIns(e.target.checked)} />
          Binnenlopers zonder afspraak mogen zich melden; de gastheer bevestigt achteraf
        </label>
        <div className="field visit-field">
          <span>Ruimte kiezen bij inklokken</span>
          <Segmented<RoomMode>
            label="Ruimte kiezen bij inklokken"
            value={roomMode}
            onChange={setRoomMode}
            items={ROOM_MODES.map((m) => ({ value: m, label: ROOM_MODE_LABELS[m] }))}
          />
          <span className="hint">
            {b.rooms.length ? "Bij een ontruiming zie je dan waar iemand is." : "Dit gebouw heeft nog geen ruimtes."}
          </span>
        </div>
        <label className="field visit-field visit-time">
          <span>Te controleren na</span>
          <input type="time" value={lateAfter} onChange={(e) => setLateAfter(e.target.value)} step={900} />
          <span className="hint">Wie na dit tijdstip nog binnen staat, komt bij Te controleren.</span>
        </label>
      </section>
      <section className="panel">
        <h2>Toegang</h2>
        <label className="field">
          <span>Toegangsinstructie</span>
          <textarea
            rows={3}
            value={accessNote}
            onChange={(e) => setAccessNote(e.target.value)}
            maxLength={1000}
            placeholder="bijv. Melden bij de receptie, sleutel in de kluis bij de laaddeur"
          />
          <span className="hint">Staat bij elk bezoek in dit gebouw.</span>
        </label>
      </section>
      <div className="btnrow">
        <button type="button" className="btn primary" onClick={() => void save()} disabled={busy || !lateAfter}>
          Opslaan
        </button>
      </div>
    </div>
  );
}
